import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, LayoutDashboard } from "lucide-react";

/**
 * Catches render/runtime errors in a subtree so one crashing panel can't blank the
 * whole app (previously an unguarded admin sub-route would white-screen everything).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    // A caller can supply a custom fallback (e.g. the marketing header + a friendly panel) so a
    // crash in one route never blanks the whole site.
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div className="max-w-[720px] mx-auto px-4 py-16 text-foreground" data-testid="error-boundary">
        <div className="border border-blaze/50 bg-blaze/5 p-8">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-blaze shrink-0" strokeWidth={1.8} />
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">Something broke on this screen</h2>
          </div>
          <p className="mt-3 text-sm font-body text-secondary-foreground">
            This section hit an unexpected error, but the rest of the app is fine. Try reloading it, or head back to the dashboard.
          </p>
          <pre className="mt-4 max-h-40 overflow-auto bg-obsidian border border-sniper p-3 font-mono text-[11px] text-muted-foreground whitespace-pre-wrap">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={this.reset}
              data-testid="error-boundary-retry"
              className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-2.5 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Try again
            </button>
            <Link
              to="/admin/dashboard"
              data-testid="error-boundary-dashboard"
              className="inline-flex items-center gap-2 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-4 py-2.5 transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
